import {
  addAsset,
  addressErrorPopup,
  addressResponseType,
  getNearestAddress,
  initialCenterWCC,
  markerIcon,
  onMapClick,
  createAssetFeatureLayer,
  CUSTODIAN_CODES,
} from '@utils';
import {
  LatLngTuple,
  LayerGroup,
  Marker,
  Popup,
  LeafletMouseEvent,
  esri,
} from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import { SearchControl } from '../MapComponent/SearchControl/SearchControl';
import { EsriTileLayer } from './EsriTileLayer/EsriTileLayer';
import 'leaflet/dist/leaflet.css';
import { AssetTypeConfig } from '@types';
import * as Sentry from '@sentry/react';

interface MapComponentProps {
  defaultLocation?: LatLngTuple;
  onChange: (location: addressResponseType) => void;
  selectedAddress?: addressResponseType;
  customAddressContent?: string | React.ReactNode;
  assetType?: AssetTypeConfig | AssetTypeConfig[];
  enableBuffer?: boolean;
  displayMapAssets?: boolean;
  enableTfLBoundaryCheck?: boolean;
}

const MapContent = ({
  onChange,
  defaultLocation,
  assetType,
  enableBuffer,
  displayMapAssets = false,
  enableTfLBoundaryCheck,
  selectedAddress,
}: Pick<
  MapComponentProps,
  | 'onChange'
  | 'defaultLocation'
  | 'assetType'
  | 'enableBuffer'
  | 'displayMapAssets'
  | 'enableTfLBoundaryCheck'
  | 'selectedAddress'
>) => {
  const map = useMap();
  const graphicsLayerRef = useRef<LayerGroup | null>(null);
  const assetLayerRef = useRef<esri.FeatureLayer[] | null>(null);

  const [searchTerm, setSearchTerm] = useState(
    selectedAddress?.full_address ?? '',
  );

  useEffect(() => {
    graphicsLayerRef.current = new LayerGroup();
    map.addLayer(graphicsLayerRef.current);

    if (displayMapAssets && assetType) {
      assetLayerRef.current = [];
      // Adds all the assets to the map
      const assetTypes = Array.isArray(assetType) ? assetType : [assetType];
      assetTypes.forEach((asset) => {
        const assetLayer = createAssetFeatureLayer(asset);
        // click event handler for asset selection and map update
        assetLayer.on('click', async (e) => {
          e.originalEvent.preventDefault();
          const feature = e.propagatedFrom.feature;
          let addressFromPin = await getNearestAddress(
            e.latlng.lat,
            e.latlng.lng,
            enableBuffer ? Object.values(CUSTODIAN_CODES) : undefined,
          );
          if (addressFromPin === null) {
            addressErrorPopup(map, e);
            return;
          }
          if (feature && feature.properties) {
            const location: addressResponseType = {
              ...addressFromPin,
              latitude: e.latlng.lat.toString(),
              longitude: e.latlng.lng.toString(),
              asset: feature.properties,
            };
            clearGraphics();
            onChange(location);
            setSearchTerm(location.full_address);
            map.flyTo(e.latlng);
            addMarker([e.latlng.lat, e.latlng.lng]);
          }
        });

        assetLayerRef.current!.push(assetLayer);
        map.addLayer(assetLayer);
      });
    }
    return () => {
      if (graphicsLayerRef.current) {
        map.removeLayer(graphicsLayerRef.current);
      }
      if (assetLayerRef.current) {
        assetLayerRef.current.forEach((layer) => {
          map.removeLayer(layer);
        });
      }
    };
  }, [map]);

  const clearGraphics = () => {
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.clearLayers();
    }
  };

  const addMarker = (latlng: LatLngTuple, popupContent?: string) => {
    const marker = new Marker(latlng, { icon: markerIcon, zIndexOffset: 1000 });
    if (popupContent) {
      const popup = new Popup({
        autoClose: true,
        autoPan: true,
        autoPanPadding: [50, 50],
      }).setContent(popupContent);

      marker.bindPopup(popup);
    }

    if (graphicsLayerRef.current !== null) {
      graphicsLayerRef.current.addLayer(marker);
      if (popupContent) {
        marker.openPopup();
      }
    }
  };

  const mapMethods = {
    clearGraphics,
    addMarker,
    onChange,
    map,
    assetType,
    graphicsLayer: graphicsLayerRef,
  };

  useEffect(() => {
    const setMapDefaultLocation = async () => {
      if (defaultLocation !== undefined && defaultLocation.length === 2) {
        try {
          const address = await getNearestAddress(
            defaultLocation[0],
            defaultLocation[1],
          );
          if (address === null) {
            addressErrorPopup(map);
            return;
          } else {
            map.setView(defaultLocation, 16);
            addMarker(defaultLocation);
            await addAsset(assetType, onChange, address);

            setSearchTerm(address.full_address);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    };

    map.on('click', async (event: LeafletMouseEvent) => {
      const targetEl = event.originalEvent.target as HTMLElement;
      const classNamesArray = Array.from(targetEl.classList || []);
      // Needed to prevent the map from getting out of focus when user clicks on the input
      const isMapElement = classNamesArray.some(
        (className) =>
          className === 'leaflet-touch' || className === 'leaflet-interactive',
      );
      if (isMapElement) {
        await onMapClick(event, mapMethods, setSearchTerm, enableBuffer);
      }
    });

    setMapDefaultLocation();
    return () => {
      map.off('click');
    };
  }, []);

  return (
    <>
      <SearchControl
        mapMethods={mapMethods}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        enableBuffer={enableBuffer}
        enableTfLBoundaryCheck={enableTfLBoundaryCheck}
      />
      <EsriTileLayer enableBuffer={enableBuffer} />
    </>
  );
};

export const MapComponent = ({
  defaultLocation,
  onChange,
  selectedAddress,
  customAddressContent,
  assetType,
  enableBuffer,
  displayMapAssets = false,
  enableTfLBoundaryCheck,
}: MapComponentProps) => {
  const initialCenter = defaultLocation || initialCenterWCC;

  return (
    <>
      <MapContainer
        center={initialCenter}
        zoom={13}
        style={{ height: '70vh', position: 'relative' }}
        className="w-full"
      >
        <MapContent
          onChange={onChange}
          defaultLocation={defaultLocation}
          assetType={assetType}
          displayMapAssets={displayMapAssets}
          enableBuffer={enableBuffer}
          enableTfLBoundaryCheck={enableTfLBoundaryCheck}
        />
      </MapContainer>

      {selectedAddress && selectedAddress.full_address && (
        <div className="mb-3" data-testid="map-display-address">
          {customAddressContent ? (
            customAddressContent
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-bodyLg font-semibold">You have selected</p>
              <p className="text-bodyLg font-normal">
                {selectedAddress.full_address}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};
